import { useEffect } from 'react';

export const SITE_URL = 'https://skyfurniturecanada.com';
export const SITE_NAME = 'Sky Furniture Canada';

const setMeta = (attribute, key, content) => {
  if (!content && content !== '') return;
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const removeMeta = (attribute, key) => {
  const element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (element) element.remove();
};

const setCanonical = (url) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
};

const setJsonLd = (data) => {
  let element = document.head.querySelector('script[data-sky-seo-jsonld]');
  if (!data) {
    if (element) element.remove();
    return;
  }
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.dataset.skySeoJsonld = 'true';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
};

const absolutize = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Reusable SEO meta manager for the SPA.
 *
 * Props:
 * - title: document title
 * - description: meta description
 * - canonical: canonical URL path (e.g. "/products/123") or full URL
 * - noindex: boolean — renders "noindex, follow" robots tag when true
 * - ogType: og:type (default "website")
 * - image: og:image / twitter:image URL (relative or absolute)
 * - jsonLd: object to serialize into the ld+json script (omit to remove)
 */
const SeoMeta = ({
  title,
  description,
  canonical,
  noindex = false,
  ogType = 'website',
  image = '',
  jsonLd = null,
}) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

    if (canonical) {
      const canonicalUrl = absolutize(canonical);
      setCanonical(canonicalUrl);
      setMeta('property', 'og:url', canonicalUrl);
    }

    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:type', ogType);
    if (title) setMeta('property', 'og:title', title);
    if (description) setMeta('property', 'og:description', description);
    if (image) {
      setMeta('property', 'og:image', absolutize(image));
    } else {
      removeMeta('property', 'og:image');
    }
    setMeta('name', 'twitter:card', 'summary_large_image');
    if (title) setMeta('name', 'twitter:title', title);
    if (description) setMeta('name', 'twitter:description', description);

    setJsonLd(jsonLd);
  }, [title, description, canonical, noindex, ogType, image, jsonLd]);

  return null;
};

export default SeoMeta;
